import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Warehouse, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const API = process.env.REACT_APP_BACKEND_URL;
const download = async (path, filename) => {
  const res = await fetch(`${API}${path}`, { credentials: "include",
    headers: { "Authorization": `Bearer ${localStorage.getItem("erp_token") || ""}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob(); const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

export default function StoreWiseStock() {
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [items, setItems] = useState({});
  const [f, setF] = useState({ store: "all", company: "all", q: "" });

  useEffect(() => {
    (async () => {
      try {
        const [sw, st, co, it] = await Promise.all([
          api.get("/stocks/store-wise"), api.get("/stores"),
          api.get("/companies"), api.get("/stocks"),
        ]);
        setRows(sw.data); setStores(st.data); setCompanies(co.data);
        const m = {}; (it.data.items || []).forEach(i => m[i.id] = i);
        setItems(m);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, []);

  const companyName = useMemo(() => {
    if (f.company === "all") return null;
    return (companies.find(c => c.id === f.company) || {}).name || null;
  }, [f.company, companies]);

  const filtered = useMemo(() => rows.filter(r => {
    if (f.store !== "all" && r.store_id !== f.store) return false;
    if (companyName && (items[r.item_id]?.company || "") !== companyName) return false;
    if (f.q) {
      const s = f.q.trim().toLowerCase();
      if (!(r.item_name.toLowerCase().includes(s) || (r.part_number || "").toLowerCase().includes(s))) return false;
    }
    return true;
  }).map(r => ({ ...r, value: Math.round(r.qty * (items[r.item_id]?.mrp || 0) * 100) / 100 })),
  [rows, f, items, companyName]);
  const pg = usePagination(filtered);

  const totalQty = filtered.reduce((a, r) => a + (r.qty || 0), 0);
  const totalValue = filtered.reduce((a, r) => a + (r.value || 0), 0);

  const exportCsv = () => {
    const p = new URLSearchParams();
    if (f.store !== "all") p.set("store_id", f.store);
    if (f.company !== "all") p.set("company_id", f.company);
    download(`/api/stocks/store-wise/export.csv?${p}`, "store_wise_stock.csv").catch(e => toast.error(formatErr(e)));
  };

  return (
    <div className="p-8 print:p-4">
      <div className="flex items-end justify-between mb-6 print:mb-3">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2 print:hidden"><Warehouse className="w-3 h-3"/> Stocks · Stock Data</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Store Wise Stock</h1>
          <div className="text-xs text-muted-foreground mt-1">Stock grouped by store or godown</div>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button data-testid="store-stock-export" variant="outline" onClick={exportCsv} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> Export CSV</Button>
          <Button data-testid="store-stock-print" variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 border border-border bg-card p-3 rounded-sm print:hidden">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Store</Label>
          <Select value={f.store} onValueChange={(v) => setF(x => ({ ...x, store: v }))}>
            <SelectTrigger data-testid="store-stock-filter-store" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All stores</SelectItem>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Company</Label>
          <Select value={f.company} onValueChange={(v) => setF(x => ({ ...x, company: v }))}>
            <SelectTrigger data-testid="store-stock-filter-company" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All companies</SelectItem>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Search Item</Label>
          <Input data-testid="store-stock-search" value={f.q} onChange={(e) => setF(x => ({ ...x, q: e.target.value }))} placeholder="Item / Part…" className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="store-stock-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Store Name</th>
              <th className="text-left p-3">Item Name</th>
              <th className="text-left p-3">Part No</th>
              <th className="text-left p-3">Unit</th>
              <th className="text-right p-3">Quantity</th>
              <th className="text-right p-3">Value</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No stock records</td></tr>}
            {pg.pageItems.map((r, i) => (
              <tr key={`${r.store_id}-${r.item_id}-${i}`} data-testid={`store-row-${r.store_id}-${r.item_id}`} className="border-t border-border hover:bg-secondary/40">
                <td className="p-3">{r.store_name}</td>
                <td className="p-3">{r.item_name}</td>
                <td className="p-3 font-mono text-xs text-primary uppercase">{r.part_number}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.unit}</td>
                <td className={`p-3 text-right font-mono font-semibold ${r.low ? "text-destructive" : ""}`}>{r.qty}</td>
                <td className="p-3 text-right font-mono">₹{(r.value || 0).toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border bg-secondary/50 font-semibold">
                <td colSpan={4} className="p-3 text-right uppercase tracking-wider text-xs">Totals</td>
                <td className="p-3 text-right font-mono" data-testid="store-stock-total-qty">{totalQty}</td>
                <td className="p-3 text-right font-mono" data-testid="store-stock-total-value">₹{totalValue.toLocaleString("en-IN")}</td>
              </tr>
            </tfoot>
          )}
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
